import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../assets/js/LanguageContext';
import SectionEyebrow from '../common/SectionEyebrow';
import DonutChart from '../common/DonutChart';
import LineChart from '../common/LineChart';
import MiniChart from '../common/MiniChart';
import '../../assets/styles/SectionBand.css';
import '../../assets/styles/HomeSampleCharts.css';

/* Paleta de los graficos.
 *
 * La seccion ya no va sobre el panel morado sino sobre la misma franja clara
 * que el generador de preguntas, asi que los colores salen de los tokens y no
 * de los blancos translucidos de antes. */
const VIZ = {
  positive: '#2563EB',
  neutral: '#8C8983',
  negative: '#BA1A1A'
};

/* Un color por emocion, siguiendo la lectura de siempre: la alegria calida, la
   tristeza azul, la ira roja. Cada barra lo usa en claro cuando esta en reposo
   y a pleno color al señalarla. */
const EMOTION_COLORS = {
  joy: '#F59E0B',
  sadness: '#2563EB',
  fear: '#4F46E5',
  anger: '#BA1A1A',
  disgust: '#15803D',
  trust: '#0D9488',
  surprise: '#C026D3'
};


const HomeSampleCharts = () => {
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 830);
  // El tramo resaltado se guarda por etiqueta y lo comparten el anillo y la
  // leyenda: se puede señalar desde cualquiera de los dos.
  const [hoveredLabel, setHoveredLabel] = useState(null);
  /* Las dos vistas de la tarjeta izquierda. Barras y lineas no caben en un
     mismo grafico: las barras van por emocion y las lineas por fecha, asi que
     comparten tarjeta pero no ejes, y se cambia entre ellas. */
  const [view, setView] = useState('lines');

  /* Datos de muestra de la portada. No son numeros al azar: cuentan lo que
     hace el producto en seis mediciones. Se empieza con la ira y la tristeza
     arriba, se mide, se actua, y para la sexta la alegria y la confianza han
     cambiado el sitio con ellas. Por eso las lineas se cruzan por el medio:
     ese cruce es el argumento de la seccion. */
  const periods = [
    t('homecharts_period_1'), t('homecharts_period_2'), t('homecharts_period_3'),
    t('homecharts_period_4'), t('homecharts_period_5'), t('homecharts_period_6')
  ];

  const emotionSeries = [
    { label: t('homecharts_anger'), color: EMOTION_COLORS.anger, values: [8, 7, 5, 3, 2, 1] },
    { label: t('homecharts_sadness'), color: EMOTION_COLORS.sadness, values: [7, 6, 5, 4, 3, 2] },
    { label: t('homecharts_joy'), color: EMOTION_COLORS.joy, values: [2, 3, 4, 6, 8, 9] },
    { label: t('homecharts_trust'), color: EMOTION_COLORS.trust, values: [1, 2, 4, 6, 8, 10] },
  ];

  /* La foto del final del recorrido que cuenta el grafico de la izquierda: si
     alli acaban contentos, aqui no puede seguir mandando el negativo. */
  const sentimentData = [
    { label: t('homecharts_positive'), value: 62, color: VIZ.positive },
    { label: t('homecharts_neutral'), value: 23, color: VIZ.neutral },
    { label: t('homecharts_negative'), value: 15, color: VIZ.negative },
  ];

  /* La ultima de las seis mediciones, emocion a emocion: es la foto de donde
     se acaba, y por eso manda la confianza. */
  const latestEmotions = [
    { label: t('homecharts_joy'), value: 9, color: EMOTION_COLORS.joy },
    { label: t('homecharts_sadness'), value: 2, color: EMOTION_COLORS.sadness },
    { label: t('homecharts_fear'), value: 2, color: EMOTION_COLORS.fear },
    { label: t('homecharts_anger'), value: 1, color: EMOTION_COLORS.anger },
    { label: t('homecharts_disgust'), value: 1, color: EMOTION_COLORS.disgust },
    { label: t('homecharts_trust'), value: 10, color: EMOTION_COLORS.trust },
    { label: t('homecharts_surprise'), value: 4, color: EMOTION_COLORS.surprise },
  ].sort((a, b) => a.value - b.value);

  const sentimentTotal = sentimentData.reduce((sum, d) => sum + d.value, 0);
  const activeSegment = sentimentData.find((d) => d.label === hoveredLabel);

  const centerValue = activeSegment ? activeSegment.value : sentimentTotal;
  const centerLabel = activeSegment ? activeSegment.label : t('homecharts_sentiment_total');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 830);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section className="section-band">
      <div className="section-band__inner">
        <div className="section-band__head">
          <SectionEyebrow>{t('homecharts_title')}</SectionEyebrow>
        </div>

        <div className="charts-grid">
          <div className="charts-card">
            <div className="charts-card__head">
              <h3 className="charts-card__title">
                {view === 'lines'
                  ? t('homecharts_evolution_title')
                  : t('homecharts_latest_title')}
              </h3>

              <div className="charts-toggle" role="group" aria-label={t('homecharts_view_label')}>
                <button
                  type="button"
                  className={`charts-toggle__btn ${view === 'lines' ? 'is-active' : ''}`}
                  onClick={() => setView('lines')}
                  aria-pressed={view === 'lines'}
                >
                  {t('homecharts_view_evolution')}
                </button>
                <button
                  type="button"
                  className={`charts-toggle__btn ${view === 'bars' ? 'is-active' : ''}`}
                  onClick={() => setView('bars')}
                  aria-pressed={view === 'bars'}
                >
                  {t('homecharts_view_latest')}
                </button>
              </div>
            </div>

            {view === 'lines' ? (
              <LineChart
                series={emotionSeries}
                categories={periods}
                title={t('homecharts_evolution_legend')}
                height={isMobile ? 140 : 160}
                className="charts-card__chart"
              />
            ) : (
              <MiniChart
                data={latestEmotions}
                showGrid
                axisMax={10}
                title={t('homecharts_emotions_legend')}
                height={isMobile ? 130 : 160}
                className="charts-card__chart"
              />
            )}
          </div>

          <div className="charts-card">
            <h3 className="charts-card__title">{t('homecharts_sentiment_title')}</h3>

            <div className="sentiment-donut">
              <DonutChart
                data={sentimentData}
                size={isMobile ? 170 : 200}
                strokeWidth={isMobile ? 22 : 26}
                animationDuration={1.2}
                animationDelayPerSegment={0.05}
                activeLabel={hoveredLabel}
                onSegmentHover={(segment) => setHoveredLabel(segment ? segment.label : null)}
                centerContent={
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={centerLabel}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2, ease: 'circOut' }}
                      className="sentiment-donut__center"
                    >
                      <p className="sentiment-donut__label">{centerLabel}</p>
                      <p className="sentiment-donut__value">{centerValue}</p>
                    </motion.div>
                  </AnimatePresence>
                }
              />
            </div>

            <ul className="sentiment-legend">
              {sentimentData.map((segment, index) => (
                <motion.li
                  key={segment.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2 + index * 0.1, duration: 0.4 }}
                  className={`sentiment-legend__item ${hoveredLabel === segment.label ? 'is-active' : ''}`}
                  onMouseEnter={() => setHoveredLabel(segment.label)}
                  onMouseLeave={() => setHoveredLabel(null)}
                >
                  <span className="sentiment-legend__name">
                    <span
                      className="sentiment-legend__dot"
                      style={{ backgroundColor: segment.color }}
                      aria-hidden="true"
                    />
                    {segment.label}
                  </span>
                  <span className="sentiment-legend__value">{segment.value}%</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeSampleCharts;
